/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * A React functional component that renders a notice management form.
 * This component utilizes various hooks from the React library, such as useEffect, useMemo, useRef, and useState.
 * It also imports necessary components and styles from external libraries and files.
 * @returns The rendered notice management form.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { components } from "react-select";
// import "./createNoticeManagement.scss";
import InputOption from "../../components/inputOption/inputOption";
import Constant from "../../util/constant";
import CreateNoticeComponent from "./createNoticeComponent";
import { getDepotsList } from "../../store/depots/actions";
import { getNoticeTypeList } from "../../store/noticeType/actions";
import { addNotice } from "../../store/noticeManagement/actions";
import moment from "moment";
import CustomeOptionLabel from "../../components/customeOptionLabel/customeOptionLabel";
import AWS from "aws-sdk";
import awsConfig from "../../helpers/aws-exports";
import { Helmet } from 'react-helmet';

// Configure AWS SDK
/**
 * Updates the AWS configuration with the provided region, access key ID, and secret access key.
 * @param {{string}} awsConfig.aws_region - The AWS region to set in the configuration.
 * @param {{string}} process.env.REACT_APP_AWS_ACCESS_KEY_ID - The access key ID to set in the configuration.
 * @param {{string}} process.env.REACT_APP_AWS_SECRET_ACCESS_KEY - The secret access key to set in the configuration.
 * @returns None
 */
AWS.config.update({
  region: awsConfig.aws_region,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
});

const uploadDocumentValid = {
  "application/msword": [],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
  "application/pdf": [],
  "image/png": [],
  "application/x-scorm": [],
  "application/zip": []
};

/**
 * A dictionary object that maps valid thumbnail file types to their corresponding file extensions.
 * @type {{string: string}}
 */
const uploadThumNailValid = {
  "image/png": [],
};

/**
 * Uploads a file to an S3 bucket.
 * @param {{File}} file - The file to upload.
 * @param {{string}} folderPath - The path to the folder in the S3 bucket where the file will be stored.
 * @returns The URL of the uploaded file.
 * @throws {Error} If there is an error uploading the file to S3.
 */
const uploadToS3 = async (file, folderPath) => {
  // Validate file exists and is a File object
  if (!file || !(file instanceof File) || !file.name) {
    console.warn('Skipping S3 upload: file is not a valid File object', file);
    return null;
  }

  const s3 = new AWS.S3();
  const fileName = `${folderPath}/${file.name}`;

  const params = {
    Bucket: awsConfig.aws_user_files_s3_bucket,
    Key: fileName,
    Body: file,
    ContentType: file.type,
    ACL: "public-read", // Set the ACL to make the object publicly accessible
  };

  try {
    const data = await s3.upload(params).promise();
    return data.Location; // S3 object URL
  } catch (error) {
    console.error("Error uploading to S3:", error);
    throw error;
  }
};

/**
 * A functional component that renders a form for creating a notice container.
 * @param {Object} props - The props object.
 * @returns {JSX.Element} - The rendered component.
 */
const CreateNoticeContainer = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  let triggerRef = useRef(null);
  const selectInputRef = useRef();
  const noticeSelectInputRef = useRef();
  const depotList = useSelector((state) => state?.Depots?.depotsList?.data);
  const noticeTypeList = useSelector(
    (state) => state?.NoticeType?.noticeList?.data
  );
  const [noticeForm, setNoticeForm] = useState(null);
  const [headerHeight, setHeaderHeight] = useState("");
  const [placeHolder, setPlaceHolder] = useState(true);
  const [expirydatetime, setExpirydatetime] = useState();
  const [ispriority, setIspriority] = useState(t("HIGH"));
  const [textTitlecount, setTextTitlecount] = useState(0);
  const [isinvalidfile, setIsinvalidfile] = useState(false);
  const [noticeTypeName, setNoticeTypeName] = useState([]);
  const [selectDepotName, setSelectDepotName] = useState([]);
  const [dimenstionError, setDimenstionError] = useState(false);
  const [issignRequired, setIsignRequired] = useState(t("YES"));
  const [usersigndatetime, setUsersigndatetime] = useState("");
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [textSubTitlecount, setTextSubTitlecount] = useState(0);
  const [textdescriptioncount, setTextdescriptioncount] = useState(0);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [uploadedDocumentFile, setuploadedDocumentFile] = useState(null);
  const [isroleselect, setIsroleselect] = useState(t("UPLOAD_DOCUMENT"));
  const [isUploadingDocumentLink, setIsUploadingDocumentLink] = useState(false);
  const [profileImage, setProfileImage] = useState();
  const [uploadedDocumentThumnailFile, setuploadedDocumentThumnailFile] =
    useState(null);
  const [iscreateNoticeLoaderButton, setIscreateNoticeLoaderButton] =
    useState(false);

  /**
   * Executes a side effect after the component has rendered.
   * The side effect sets the `placeHolder` state to `false` after a delay of 2000 milliseconds.
   * @returns None
   */
  useEffect(() => {
    setTimeout(() => {
      setPlaceHolder(false);
    }, 2000);
  });

  /**
   * A React useEffect hook that sets the height of the header and breadcrumb elements.
   * @returns None
   */
  useEffect(() => {
    setHeaderHeight(
      document.getElementsByClassName("navbar-header")[0].offsetHeight
    );
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0].offsetHeight
    );
  }, []);

  useEffect(() => {
    getList();
  }, []);

  /**
   * Retrieves a list of depots and notice types with the status set to 'Active' using the
   * dispatch function. The results are passed to the success callback function and any
   * errors are logged to the console.
   * @returns None
   */
  const getList = () => {
    let queryParams = {};
    queryParams.status = Constant.GENERALKEYS.ACTIVE;
    dispatch(
      getDepotsList(
        queryParams,
        (res) => { },
        (err) => console.log(err)
      )
    );

    dispatch(
      getNoticeTypeList(
        queryParams,
        (res) => { },
        (err) => console.log(err)
      )
    );
  };

  /**
   * Navigates to the "/noticemanagement" page.
   * @returns None
   */
  const backPageFunction = () => {
    navigate("/noticemanagement");
  };

  /**
   * Sets the notice form state to the provided form object.
   * @param {{object}} form - The form object to set as the notice form state.
   * @returns None
   */
  const createNoticeForm = (form) => {
    setNoticeForm(form);
  };

  /**
   * Event handler for the change event of the Roleradio component.
   * Updates the selected role, clears the uploaded user file, resets the notice form,
   * and sets the value of the documentfile field to an empty array.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const roleradioChangeHandler = (e) => {
    setIsroleselect(e.target.value);
    handleClearUploadedUserFile();
    setIsUploadingDocument(false);
    setuploadedDocumentFile(null);
    noticeForm.setValue("documentfile", " ", {
      shouldValidate: false,
    });
    noticeForm.setValue("addink", "", {
      shouldValidate: true,
    });
  };

  /**
   * Event handler for the sign required change event.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const signRequiredChangeHandler = (e) => {
    setIsignRequired(e.target.value);
    noticeForm.setValue("fullname", addNoticeForm.usersign, {
      shouldValidate: true,
    });
  };

  /**
   * Event handler for changing the priority value.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const priorityChangeHandler = (e) => {
    setIspriority(e.target.value);
  };

  /**
   * Handles the change event for the text title input field.
   * Updates the count of characters in the input value.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const handleTextTitleChange = (e) => {
    const value = e.target.value;
    setTextTitlecount(value?.length);
  };

  /**
   * Handles the change event of a subtitle input field.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const handleSubtitleChange = (e) => {
    const value = e.target.value;
    setTextSubTitlecount(value?.length);
  };

  /**
   * Handles the change event of a text description input field.
   * Updates the character count of the input value.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const handleTextdescriptionChange = (e) => {
    const value = e.target.value;
    setTextdescriptioncount(value?.length);
  };

  /**
   * Creates a memoized object that represents the form fields and their validation rules
   * for adding a notice.
   * @param {{string}} issignRequired - Indicates whether a signature is required for the notice.
   * @returns An object representing the form fields and their validation rules.
   */
  const addNoticeForm = useMemo(
    () => ({
      documentfile: {
        name: "documentfile",
        validate: {
          required: {
            value: isroleselect === "Add Link" ? false : true,
          },
        },
      },
      addlink: {
        name: "addink",
        validate: {
          required: {
            value: true,
          },
          pattern: {
            value: Constant.REGEX.NOTICE_URL,
          },
        },
      },
      thumbnailfile: {
        name: "thumbnailfile",
        validate: {
          required: {
            value: false,
          },
        },
      },


      noticetype: {
        name: "noticetype",
        validate: {
          required: {
            value: true,
          },
        },
      },
      title: {
        name: "title",
        validate: {
          required: {
            value: true,
          },
          maxLength: {
            value: 100,
          },
        },
      },
      subtitle: {
        name: "subtitle",
        validate: {
          required: {
            value: false,
          },
          maxLength: {
            value: 200,
          },
        },
      },

      description: {
        name: "description",
        validate: {
          required: {
            value: true,
          },
          maxLength: {
            value: 250,
          },
        },
      },

      depot: {
        name: "depot",
        validate: {
          required: {
            value: true,
          },
        },
      },
      expirydate: {
        name: "expirydate",
        validate: {
          required: {
            value: false,
          },
        },
      },
      usersign: {
        name: "usersign",
        validate: {
          required: {
            value: issignRequired === "No" ? false : true,
          },
        },
      },
      fullname: {
        name: "fullname",
        validate: {
          required: {
            value: issignRequired === "No" ? false : true,
          },
        },
      },
      priority: {
        name: "priority",
        validate: {
          required: {
            value: false,
          },
        },
      },
      signrequired: {
        name: "signrequired",
        validate: {
          required: {
            value: false,
          },
        },
      },
    }),
    [issignRequired]
  );

  /**
   * Executes a side effect function when the dependencies change.
   * @param {Function} effect - The side effect function to execute.
   * @param {Array} dependencies - The dependencies that trigger the side effect when changed.
   * @returns None
   */
  useEffect(() => { }, [addNoticeForm, issignRequired]);

  /**
   * A custom input option component for a select dropdown.
   * @param {{function}} getStyles - A function that returns the styles for the component.
   * @param {{React.Component}} Icon - An optional icon component to display.
   * @param {{boolean}} isDisabled - Indicates if the option is disabled.
   * @param {{boolean}} isSelected - Indicates if the option is selected.
   * @param {{React.ReactNode}} children - The content to display within the option.
   * @param {{object}} innerProps - Additional props to be passed to the inner component.
   * @param {{object}} rest - Additional props to be passed to the component.
   * @returns The rendered input option component.
   */

  /**
   * Clears the uploaded user file by setting the uploadedDocumentFile state to null.
   * @returns None
   */
  const handleClearUploadedUserFile = () => {
    setuploadedDocumentFile(null);
    noticeForm.setValue("documentfile", noticeForm.documentfile, {
      shouldValidate: true,
    });
  };

  /**
   * Clears the uploaded thumbnail file by setting it to null.
   * @returns None
   */
  const handleClearUploadedThumnailFile = () => {
    setuploadedDocumentThumnailFile(null);
    setDimenstionError(false);
  };

  /**
   * Handles the upload of a document file.
   * @param {{FileList}} files - The list of files to upload.
   * @returns None
   */
  const handleUploadDocumentFile = (files) => {
    setuploadedDocumentFile(files);
  };

  /**
   * Handles the upload of a thumbnail file.
   * @param {{File}} files - The file(s) to be uploaded.
   * @returns None
   */
  // const handleUploadThumnailFile = (files) => {
  //   setuploadedDocumentThumnailFile(files);
  //   const reader = new FileReader();
  //   reader.addEventListener("load", (event) => {
  //     setProfileImage(event.target.result);
  //   });
  //   reader.readAsDataURL(files[0]);
  // };
  const handleUploadThumnailFile = (files) => {
    if (files && files?.length > 0) {
      // Files were selected
      setuploadedDocumentThumnailFile(files);
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        setProfileImage(event?.target?.result);
      });
      reader.readAsDataURL(files[0]);
    } else {
      // No files were selected (set to null or undefined)
      setuploadedDocumentThumnailFile(null); // or setuploadedDocumentThumnailFile(undefined);
      setProfileImage(null); // or setProfileImage(undefined);
    }
  };


  /**
   * Handles the upload of a document by calling the provided upload function after a delay of 3 seconds.
   * @param {{FileList}} files - The list of files to upload.
   * @param {{function}} loaderState - A function to update the loader state.
   * @param {{function}} uploadFunction - The function to handle the upload of the files.
   * @returns None
   */
  const handleUploadDocument = (files, loaderState, uploadFunction) => {
    loaderState(true);
    setTimeout(() => {
      uploadFunction(files);
      loaderState(false);
    }, 1000);
  };

  /**
   * Handles the upload of a thumbnail image file.
   * @param {{FileList}} files - The list of files to upload.
   * @param {{function}} loaderState - The function to update the loader state.
   * @param {{function}} uploadFunction - The function to handle the upload of the files.
   * @returns None
   */
  const handleUploadThumnail = (files, loaderState, uploadFunction) => {
    loaderState(true);
    setTimeout(() => {
      uploadFunction(files);
      loaderState(false);
    }, 1000);
  };

  /**
   * Uploads the document and thumbnail files to an S3 bucket.
   * @param {{any}} document - The document file to upload.
   * @param {{any}} thumbnail - The thumbnail file to upload.
   * @returns None
   */
  const getDocAndthum = async (document, thumbnail) => {
    try {
      let documentFile
      let thumbnailFile
      if (uploadedDocumentFile) {
        documentFile = uploadedDocumentFile[0];
      }
      if (uploadedDocumentThumnailFile) {
        thumbnailFile = uploadedDocumentThumnailFile[0]
      }

      // Validate files exist before uploading
      if (document !== " " && thumbnail === undefined && documentFile) {
        await uploadToS3(documentFile, "document");
      } else if (document !== " " && thumbnail !== undefined && thumbnail?.length > 0 && documentFile) {
        await uploadToS3(documentFile, "document");
        if (thumbnailFile) {
          await uploadToS3(thumbnailFile, "thumbnail");
        }
      } else if (document === " " && thumbnail !== undefined && thumbnailFile) {
        await uploadToS3(thumbnailFile, "thumbnail");
      }
    } catch (error) {
      throw error; // Re-throw to be caught by handleFormSubmit
    }
  }

  /**
   * Handles the form submission by setting the "isCreateNoticeLoaderButton" state to true,
   * displaying a success toast message, resetting the notice form fields, and navigating to
   * the "/noticemanagement" page after a delay of 1000 milliseconds.
   * @param {{object}} data - The form data submitted.
   * @returns None
   */
  const handleFormSubmit = async (data) => {
    console.log('data', data);
    setIscreateNoticeLoaderButton(true);
    try {
      console.log('Uploading document and thumbnail files to S3...');
      await getDocAndthum(data?.documentfile, data?.thumbnailfile);
      console.log('Document and thumbnail files uploaded to S3 successfully');
    } catch (error) {
      // S3 upload failed - stop loader and show error
      setIscreateNoticeLoaderButton(false);
      console.error('S3 upload failed:', error);
      // Error toast will be shown by the saga
      return; // Exit early, don't proceed with API call
    }
    
    console.log('Compiling upload parameters...');
    const depotId = data?.depot?.map((item) => item?.depotId);
    let finalobj = {};
    Object.keys(data).forEach(itm => {
      if (data[itm] !== "" && data[itm] !== null && data[itm] !== undefined && data[itm] !== " ") {
        finalobj[itm] = data[itm]
      }
    })
    const params = {
      noticetypeid: data?.noticetype?.noticeTypeId,
      title: data?.title,
      description: data?.description,
      depotid: depotId,
      priority: data?.priority,
      signrequired: data?.signrequired === "Yes" ? true : false,
    };

    console.log('Upload parameters assembled...');

    if (data?.subtitle) {
      params['subtitle'] = data?.subtitle
    }
    if (data?.addink) {
      params['noticelink'] = data?.addink
    }
    if (data?.documentfile) {
      params['documenturl'] = data?.documentfile[0]?.name
    }
    if (data?.thumbnailfile || data?.thumbnailfile?.length > 0) {
      params['documentthumb'] = data?.thumbnailfile[0]?.path
    }
    if (data?.expirydate || (data?.expirydate !== 'Invalid date')) {
      params['expirydatetime'] = moment.utc(data?.expirydate).format('YYYY-MM-DD')
    }
    if (data?.usersign || data?.usersign !== '') {
      params['signtill'] = moment.utc(data?.usersign).format('YYYY-MM-DD')
    }

    console.log('Additional data attached. Dispatching upload...');

    dispatch(
      addNotice(
        params,
        (res) => {
          setIscreateNoticeLoaderButton(false);
          navigate("/noticemanagement");
          noticeForm.reset(
            {
              documentfile: [],
              addink: "",
              title: "",
              noticetype: "",
              subtitle: "",
              description: "",
              depot: "",
              usersign: "",
              expirydate: "",
            },
            noticeSelectInputRef.current.clearValue(),
            selectInputRef.current.clearValue(),
            handleClearUploadedUserFile(),
            handleClearUploadedThumnailFile(),
            setTextTitlecount(0),
            setTextSubTitlecount(0),
            setTextdescriptioncount(0),
            setExpirydatetime(""),
            setUsersigndatetime("")
          );
          setIscreateNoticeLoaderButton(false);
        },
        (err) => {
          setIscreateNoticeLoaderButton(false);
          console.log(err);
        }
      )
    );
  };

  /**
   * useEffect hook that runs once when the component mounts. It sets the initial value of
   * the usersigndatetime state variable by formatting the initialDate obtained from the
   * updatenoticeData object using the formatDate function.
   * @param None
   * @returns None
   */
  // useEffect(() => {
  //   const initialDate = usersigndatetime;
  //   const initialFormattedDate = formatDate(initialDate);
  //   setUsersigndatetime(initialFormattedDate);
  // }, [usersigndatetime]);

  /**
   * Handles the change event for the user sign input field.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  function handleChangeUsersign(e) {
    if (!e.target.validity.valid) return;
    const dt = e.target.value;
    const formattedDate = formatDate(dt);
    setUsersigndatetime(dt);
  }

  /**
   * useEffect hook that runs once when the component mounts. It retrieves the initial
   * expiry date from the updatenoticeData object, formats it using the formatDate function,
   * and sets the formatted date as the value of the expirydatetime state variable.
   * @returns None
   */
  // useEffect(() => {
  //   const initialDate = expirydatetime;
  //   const initialFormattedDate = formatDate(initialDate);
  //   setExpirydatetime(initialFormattedDate);
  // }, [expirydatetime]);

  const setFormatDate = (dateString) => {
    const date = new Date(dateString);

    // Set the time to 11:59 PM
    date.setHours(23, 59, 0, 0);

    // Format the date to the desired output
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  /**
   * Event handler for the change event of the expiry date input field.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  function onChangeExpiryDate(e) {
    if (!e.target.validity.valid) return;

    const dt = e.target.value;
    // let date = new Date(dt);
    // date.setHours(23, 59, 0, 0);


    // console.log('dt: ', dt);
    // const formattedDate = setFormatDate(dt);
    // console.log('formattedDate: ', formattedDate);
    setExpirydatetime(dt);
  }

  /**
   * Formats a given date into a specific format: "day month year, hours:minutes AM/PM".
   * @param {{Date}} inputDate - The date to be formatted.
   * @returns The formatted date string.
   */
  function formatDate(inputDate) {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dateObject = new Date(inputDate);
    const day = dateObject.getDate();
    const month = months[dateObject.getMonth()];
    const year = dateObject.getFullYear();
    const hours = dateObject.getHours();
    const minutes = dateObject.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    const formattedDate = `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
    return formattedDate;
  }

  /**
   * Get the current date and format it according to the specified format.
   * @returns {string} The formatted current date.
   */

  const currentDate = new Date();
  const currentFormat = "YYYY-MM-DD";
  const currentformattedDate = moment(currentDate).format(currentFormat);

  const expireDate = expirydatetime;
  const expireFormat = "YYYY-MM-DD";
  const expireformattedDate = moment(expireDate).format(expireFormat);
  console.log('expireformattedDate', expireformattedDate)
  /**
   * Renders a component that allows the user to create a notice.
   * @param {{string}} innerPagetitle - The title of the inner page.
   * @param {{function}} backPageFunction - The function to go back to the previous page.
   * @param {{object}} addNoticeForm - The form data for adding a notice.
   * @param {{boolean}} isroleselect - Indicates if role selection is enabled.
   * @param {{function}} roleradioChangeHandler - The function to handle role radio change.
   * @param {{object}} selectInputRef - The reference to the select input element.
   * @param {{object}} noticeSelectInputRef - The reference to the notice select input element.
   * @param {{function}}
   */
  const depotOptions = depotList?.map((item) => ({
    label: item?.name,
    value: item?.name,
    depotId: item?.depotid,
  })) || [];

  return (
    <>
      <Helmet>
        <title>Create A Notice | ScotRail Safety Notice Portal</title>
      </Helmet>
      <CreateNoticeComponent
        innerPagetitle={t("CREATE_NOTICE")}
        backPageFunction={backPageFunction}
        addNoticeForm={addNoticeForm}
        isroleselect={isroleselect}
        roleradioChangeHandler={(e) => {
          roleradioChangeHandler(e);
        }}
        selectInputRef={selectInputRef}
        noticeSelectInputRef={noticeSelectInputRef}
        formatOptionLabel={<CustomeOptionLabel />}
        components={{ Option: InputOption }}
        textTitlecount={textTitlecount}
        handleTextTitleChange={handleTextTitleChange}
        textSubTitlecount={textSubTitlecount}
        handleSubtitleChange={handleSubtitleChange}
        handleTextdescriptionChange={handleTextdescriptionChange}
        textdescriptioncount={textdescriptioncount}
        optionNoticeName={noticeTypeList?.map((item) => {
          return {
            label: item?.name,
            value: item?.name,
            noticeTypeId: item?.noticetypeid,
          };
        })}
        onChangeNoticeType={(options) => {
          if (Array.isArray(options)) {
            setNoticeTypeName(options.map((opt) => opt.value));
          }
        }}
        optionDepotName={[
          { label: t("SELECT_ALL"), value: "select-all" },
          ...depotOptions
        ]}
        onChangeDepotName={(selectedOptions) => {
          const hasSelectAll = selectedOptions?.some((opt) => opt.value === "select-all");
          let resolved;

          if (hasSelectAll) {
            const realSelected = selectedOptions.filter((opt) => opt.value !== "select-all");
            resolved = realSelected.length >= depotOptions.length ? [] : depotOptions;
          } else {
            resolved = selectedOptions || [];
          }

          setSelectDepotName(resolved.map((opt) => opt.value));
          return resolved;
        }}
        issignRequired={issignRequired}
        signRequiredChangeHandler={(e) => {
          signRequiredChangeHandler(e);
        }}
        ispriority={ispriority}
        priorityChangeHandler={priorityChangeHandler}
        handleUploadDocumentFile={handleUploadDocumentFile}
        handleClearUploadedUserFile={handleClearUploadedUserFile}
        uploadedDocumentFile={uploadedDocumentFile}
        uploadDocumentValid={uploadDocumentValid}
        uploadThumNailValid={uploadThumNailValid}
        handleUploadDocument={handleUploadDocument}
        handleUploadThumnail={handleUploadThumnail}
        handleUploadThumnailFile={handleUploadThumnailFile}
        uploadedDocumentThumnailFile={uploadedDocumentThumnailFile}
        noticeTypeName={noticeTypeName}
        selectDepotName={selectDepotName}
        profileImage={profileImage}
        createNoticeFunction={createNoticeForm}
        handleFormSubmit={handleFormSubmit}
        isUploadingDocument={isUploadingDocument}
        isUploadingDocumentLink={isUploadingDocumentLink}
        isinvalidfile={isinvalidfile}
        dimenstionError={dimenstionError}
        setDimenstionError={setDimenstionError}
        setIsUploadingDocument={setIsUploadingDocument}
        setIsUploadingDocumentLink={setIsUploadingDocumentLink}
        setIsinvalidfile={setIsinvalidfile}
        createNoticeLoaderButton={iscreateNoticeLoaderButton}
        handleClearUploadedThumnailFile={handleClearUploadedThumnailFile}
        triggerRef={triggerRef}
        headerHeight={headerHeight}
        breadcrumbHeight={breadcrumbHeight}
        currentformattedDate={currentformattedDate}
        expirydatetime={expirydatetime}
        onChangeExpiryDate={onChangeExpiryDate}
        usersigndatetime={usersigndatetime}
        handleChangeUsersign={(ev) => {
          handleChangeUsersign(ev);
        }}
        expireformattedDate={expireformattedDate}
        placeHolderShimer={placeHolder}
      />
    </>
  );
};

export default CreateNoticeContainer;
